import * as React from 'react';
import {createPortal} from 'react-dom';
import type {Placement as FloatingPlacement} from '@floating-ui/react';

import {TooltipReact} from './index';
import {Placement, PositioningStrategy, TooltipTextAlign} from './types';

type Props = {
  content: null | string | React.ReactElement;
  placement: FloatingPlacement;
  enabled?: boolean;
  children: React.ReactElement;
  maxWidth?: number | string;
  forceShow?: boolean;
};

/** Wrapper API for callers whose child is the tooltip trigger. */
export function Tooltip({content, placement, enabled = true, maxWidth, children, forceShow = false}: Props) {
  const [reference, setReference] = React.useState<HTMLElement | null>(null);
  const originalRef = (children as React.ReactElement & {ref?: React.Ref<HTMLElement>}).ref;
  const attachReference = React.useCallback(
    (element: HTMLElement | null) => {
      setReference(element);
      if (typeof originalRef === `function`) originalRef(element);
      else if (originalRef) (originalRef as React.MutableRefObject<HTMLElement | null>).current = element;
    },
    [originalRef],
  );
  const mappedPlacement =
    placement === `left` ? Placement.LeftCenter : placement === `right` ? Placement.RightCenter : placement;
  return (
    <>
      {React.cloneElement(children, {ref: attachReference})}
      {reference &&
        enabled &&
        content &&
        createPortal(
          <TooltipReact
            parentEl={reference}
            placement={mappedPlacement}
            positionStrategy={PositioningStrategy.Absolute}
            forceShow={forceShow}
            delayOut={100}
            hideOnMouseDown={false}
            textAlign={TooltipTextAlign.Start}
            maxWidth={typeof maxWidth === `number` ? `${maxWidth}px` : maxWidth}
            breakWord={Boolean(maxWidth)}
          >
            {content}
          </TooltipReact>,
          document.querySelector(`#tooltips`) || document.body,
        )}
    </>
  );
}
